import { useMemo, useState } from 'react';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Boxes,
  Bug,
  Building2,
  Clock3,
  CreditCard,
  Database,
  FileCode2,
  FileWarning,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  MapPin,
  MessageCircleQuestion,
  MessageSquare,
  PackagePlus,
  Send,
  Shield,
  ShieldCheck,
  UploadCloud,
  UserRoundCog,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { companyAddress, contactEmail as dataContactEmail, lastUpdated as dataLastUpdated } from '@/pages/Data';
import {
  companyAddress as eulaCompanyAddress,
  companyName as eulaCompanyName,
  contactEmail as eulaContactEmail,
  lastUpdated as eulaLastUpdated,
} from '@/pages/Eula';
import {
  DataPolicyDocumentContent,
  EulaDocumentContent,
} from '@/components/legal/LegalDocuments';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useMarketplaceMemberships } from '@/hooks/supabase/useMarketplaceMemberships';
import { useMarketplaceProducts } from '@/hooks/supabase/useMarketplaceProducts';
import { supabase } from '@/integrations/supabase/client';
import type { MapleMarketCategory } from '@/data/mapleMarketApps';
import MapleMarketFooter from '@/components/maple-market/MapleMarketFooter';
import MapleMarketNavbar, { MAPLE_MARKET_NAV_TOTAL_HEIGHT } from '@/components/maple-market/MapleMarketNavbar';
import { Button } from '@/components/ui/button';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/blueprints/ui/OSAwareSelect';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { useEffect } from 'react';

export type MapleMarketInfoPageId =
  | 'help'
  | 'contact'
  | 'refund-policy'
  | 'developers'
  | 'developers-api'
  | 'submit-app'
  | 'legal-eula'
  | 'legal-privacy'
  | 'legal-data';

interface MapleMarketInfoViewProps {
  page: MapleMarketInfoPageId;
  embedded?: boolean;
  isAdminAvailable?: boolean;
  onOpenAdmin?: () => void;
  onOpenLibrary?: () => void;
  onSelectCategory: (category: MapleMarketCategory | 'All') => void;
  onSelectApp: (appId: string) => void;
  onOpenPage?: (page: MapleMarketInfoPageId) => void;
  onBackToStore: () => void;
}

const helpResources = [
  {
    title: 'Marketplace docs',
    description: 'Read the Maple Market publishing, packaging, and admin guidance.',
    page: 'developers-api' as MapleMarketInfoPageId,
    icon: BookOpen,
  },
  {
    title: 'Community forums',
    description: 'Ask questions, share release updates, and get help from the MapleOS community.',
    href: '/forums',
    icon: MessageSquare,
  },
  {
    title: 'Bug reports',
    description: 'Report marketplace issues, broken installs, or storefront problems.',
    href: '/bugs',
    icon: Bug,
  },
  {
    title: 'Contact support',
    description: 'Reach the MapleOS team directly for account or purchase assistance.',
    page: 'contact' as MapleMarketInfoPageId,
    icon: Mail,
  },
];

const apiSections = [
  {
    title: 'Data model',
    description: 'Products are stored in marketplace_products. Team access is scoped per product through marketplace_product_members.',
    icon: Database,
    code: 'marketplace_products\nmarketplace_product_members\nroles: owner | admin | developer | moderator',
  },
  {
    title: 'Embedded admin model',
    description: 'There is no public marketplace admin route. Product management happens inside MapleOS App Store.',
    icon: ShieldCheck,
    code: 'MapleOS > Maple Market > Profile menu > Marketplace Admin',
  },
  {
    title: 'Assets',
    description: 'Hero and thumbnail assets upload into the marketplace storage bucket using SKU-based paths.',
    icon: UploadCloud,
    code: 'bucket: marketplace\nproducts/<sku>/hero.<ext>\nproducts/<sku>/thumbnail.<ext>',
  },
  {
    title: 'Package delivery',
    description: 'Use package_type and package_path to distinguish a single HTML bundle from a .mpak upload with its own root manifest.',
    icon: Boxes,
    code: 'bundle -> products/my-app/1.0.0/index.html\npackage -> products/my-app/1.0.0/app.mpak',
  },
];

const submitAppSchema = z.object({
  studioName: z.string().min(2, 'Studio or company name is required.'),
  appName: z.string().min(2, 'App name is required.'),
  website: z.string().url('Enter a valid website URL.').optional().or(z.literal('')),
  category: z.string().min(1, 'Choose a category.'),
  summary: z.string().min(40, 'Tell us a bit more about your app and publishing goals.').max(2000),
});

const submitCategories = ['Games', 'Office', 'Creative', 'Developer', 'Music', 'Utilities'];

export default function MapleMarketInfoView({
  page,
  embedded = false,
  isAdminAvailable = false,
  onOpenAdmin,
  onOpenLibrary,
  onSelectCategory,
  onSelectApp,
  onOpenPage,
  onBackToStore,
}: MapleMarketInfoViewProps) {
  const navigate = useNavigate();
  const { products } = useMarketplaceProducts();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { isSiteAdmin, isVendor, memberships } = useMarketplaceMemberships();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (embedded) {
      const container = document.querySelector('.maple-market-scroll-container');
      if (container instanceof HTMLElement) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [embedded, page]);

  const contentSpacing = embedded ? 'pt-6' : undefined;
  const rootClassName = embedded ? 'min-h-full bg-background' : 'min-h-screen bg-background';
  const hasMarketplaceAccess = isSiteAdmin || isVendor;

  const form = useForm<z.infer<typeof submitAppSchema>>({
    resolver: zodResolver(submitAppSchema),
    defaultValues: {
      studioName: profile?.name || '',
      appName: '',
      website: '',
      category: '',
      summary: '',
    },
  });

  const accessMessage = useMemo(() => {
    if (isSiteAdmin) {
      return 'You are already a site admin and can manage all marketplace products from MapleOS.';
    }

    if (isVendor) {
      return `You already have marketplace access through ${memberships.length} product membership${memberships.length === 1 ? '' : 's'}.`;
    }

    return 'You are not a marketplace vendor yet. Submit your app to begin owner/vendor onboarding.';
  }, [isSiteAdmin, isVendor, memberships.length]);

  const openPage = (nextPage: MapleMarketInfoPageId) => {
    if (onOpenPage) {
      onOpenPage(nextPage);
      return;
    }

    navigate(`/market?page=${encodeURIComponent(nextPage)}`);
  };

  const openHref = (href: string) => {
    navigate(href);
  };

  const onSubmitApp = async (values: z.infer<typeof submitAppSchema>) => {
    if (!user?.email) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in before starting vendor onboarding.',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    const message = [
      'Maple Market vendor onboarding request',
      `App name: ${values.appName}`,
      `Category: ${values.category}`,
      values.summary,
    ].join('\n\n');

    const { error } = await (supabase.from('partnership_requests') as any).insert({
      company_name: values.studioName,
      contact_name: profile?.name || user.email.split('@')[0] || 'MapleOS user',
      contact_email: user.email,
      website: values.website || null,
      message,
    });

    setSubmitting(false);

    if (error) {
      toast({
        title: 'Submission failed',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }

    setSubmitted(true);
    form.reset({
      studioName: values.studioName,
      appName: '',
      website: values.website,
      category: '',
      summary: '',
    });
    toast({
      title: 'Submission received',
      description: 'Your Maple Market onboarding request has been sent to the team.',
    });
  };

  const pageMeta: Record<MapleMarketInfoPageId, { eyebrow: string; title: string; description: string }> = {
    help: {
      eyebrow: 'Support',
      title: 'Maple Market Help Center',
      description: 'Find answers for storefront browsing, installs, purchases, publishing, and vendor access from one place.',
    },
    contact: {
      eyebrow: 'Support',
      title: 'Contact Maple Market',
      description: 'Get in touch for account access, purchase questions, marketplace support, and publishing guidance.',
    },
    'refund-policy': {
      eyebrow: 'Support',
      title: 'Maple Market Refund Policy',
      description: 'Maple Market digital purchases are reviewed case by case to protect customers, developers, and the integrity of software delivery.',
    },
    developers: {
      eyebrow: 'Developers',
      title: 'Maple Market Developer Portal',
      description: 'Everything publishers need to understand vendor onboarding, product ownership, and how marketplace administration works inside MapleOS.',
    },
    'developers-api': {
      eyebrow: 'Developers',
      title: 'Maple Market API Docs',
      description: 'This Maple Market-specific docs section explains how publishing, assets, product roles, and embedded administration work.',
    },
    'submit-app': {
      eyebrow: 'Developers',
      title: 'Submit an App',
      description: 'Start the Maple Market vendor onboarding flow for a new product, owner, or publishing team.',
    },
    'legal-eula': {
      eyebrow: 'Legal',
      title: 'Maple Market EULA',
      description: 'Marketplace access and software distribution on Maple Market operate under the same core MapleOS legal framework, adapted here for marketplace usage.',
    },
    'legal-privacy': {
      eyebrow: 'Legal',
      title: 'Maple Market Privacy Policy',
      description: 'Maple Market follows MapleOS privacy principles: Canadian data posture, careful handling of account information, and transparent platform use.',
    },
    'legal-data': {
      eyebrow: 'Legal',
      title: 'Maple Market Data Policy',
      description: 'Marketplace usage follows the same MapleOS data commitments around residency, minimization, and secure handling, with extra focus on purchases, installs, and vendor roles.',
    },
  };

  const meta = pageMeta[page];

  return (
    <div className={rootClassName}>
      <MapleMarketNavbar
        products={products}
        embedded={embedded}
        isAdminAvailable={isAdminAvailable}
        onOpenAdmin={onOpenAdmin}
        onOpenLibrary={onOpenLibrary}
        onSelectCategory={onSelectCategory}
        onSelectApp={onSelectApp}
      />
      <main
        className={`container pb-8 ${contentSpacing ?? ''}`}
        style={embedded ? undefined : { paddingTop: `calc(${MAPLE_MARKET_NAV_TOTAL_HEIGHT} + 1.5rem)` }}
      >
        <div className="mx-auto max-w-5xl">

          <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
            <button type="button" onClick={onBackToStore} className="inline-flex items-center gap-2 transition-colors hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              Back to Store
            </button>
          </div>

          <div className="rounded-3xl border border-border bg-card/40 p-8 shadow-sm backdrop-blur-sm md:p-10">
            <div className="border-b border-border pb-8">
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-primary">{meta.eyebrow}</div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">{meta.title}</h1>
              <p className="mt-3 max-w-3xl text-base leading-7 text-muted-foreground">{meta.description}</p>
            </div>

            <div className="pt-8">
              {page === 'help' && (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    {helpResources.map((resource) => (
                      <Card key={resource.title}>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <resource.icon className="h-5 w-5 text-primary" />
                            {resource.title}
                          </CardTitle>
                          <CardDescription>{resource.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => (resource.page ? openPage(resource.page) : openHref(resource.href!))}
                          >
                            Open
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <Card className="mt-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <LifeBuoy className="h-5 w-5 text-primary" />
                        What we can help with
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-3 text-sm text-muted-foreground md:grid-cols-2">
                      <div className="rounded-xl border border-border p-4">Trouble installing or launching an app</div>
                      <div className="rounded-xl border border-border p-4">Questions about ownership, licenses, or purchases</div>
                      <div className="rounded-xl border border-border p-4">Marketplace vendor onboarding and app submission help</div>
                      <div className="rounded-xl border border-border p-4">Publishing assets, package paths, and release setup</div>
                    </CardContent>
                  </Card>
                </>
              )}

              {page === 'contact' && (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Mail className="h-5 w-5 text-primary" />
                          Email
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        <a href={`mailto:${dataContactEmail}`} className="font-medium text-foreground underline underline-offset-4">
                          {dataContactEmail}
                        </a>
                        <p className="mt-3">Use email for vendor onboarding, refund requests, purchase questions, and marketplace support.</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Clock3 className="h-5 w-5 text-primary" />
                          Support response
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        We usually respond within 1–2 business days. Include your MapleOS email and the product name or SKU when possible.
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Building2 className="h-5 w-5 text-primary" />
                          Company
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">mapleos.ai</CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <MapPin className="h-5 w-5 text-primary" />
                          Mailing address
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">{companyAddress}</CardContent>
                    </Card>
                  </div>

                  <Card className="mt-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <MessageCircleQuestion className="h-5 w-5 text-primary" />
                        Before you contact us
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm text-muted-foreground">
                      <p>For purchase issues, include the app name, purchase date, and the email used for your MapleOS account.</p>
                      <p>For vendor access, tell us whether you are applying as a new publisher, an owner, or an admin joining an existing product team.</p>
                    </CardContent>
                  </Card>
                </>
              )}

              {page === 'refund-policy' && (
                <>
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg"><CreditCard className="h-5 w-5 text-primary" />Eligible cases</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        Duplicate purchases, failed delivery, accidental purchases, or apps that cannot be installed as described.
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg"><FileWarning className="h-5 w-5 text-primary" />Review info</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        Include your account email, product name, SKU if available, purchase date, and a short explanation of the problem.
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg"><ShieldCheck className="h-5 w-5 text-primary" />Decision process</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        Refunds are assessed manually to verify the request and protect software publishers from abuse.
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mt-6 space-y-4 text-sm leading-7 text-muted-foreground">
                    <p>Because Maple Market distributes digital software, refunds are not automatic after download or use. We review requests fairly and will work with you when an app purchase did not perform as expected.</p>
                    <p>If the issue is technical, we may first help you resolve installation or entitlement problems before finalizing a refund outcome.</p>
                    <p>Questions or requests can be sent to <a href={`mailto:${dataContactEmail}`} className="text-foreground underline underline-offset-4">{dataContactEmail}</a>.</p>
                  </div>
                </>
              )}

              {page === 'developers' && (
                <>
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg"><LayoutDashboard className="h-5 w-5 text-primary" />Embedded admin</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        Marketplace administration lives inside MapleOS App Store, not on a public /admin page.
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg"><Shield className="h-5 w-5 text-primary" />Role model</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        Site admins have global control. Product owners and admins manage listings and team access for their products.
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg"><PackagePlus className="h-5 w-5 text-primary" />Vendor onboarding</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        New publishers start with a submission request, then receive owner access once their product setup is approved.
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="mt-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg"><UserRoundCog className="h-5 w-5 text-primary" />Your current access</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm text-muted-foreground">
                      {hasMarketplaceAccess ? (
                        <>
                          <p>You already have Maple Market admin eligibility{isSiteAdmin ? ' as a site admin' : ''}.</p>
                          {!isSiteAdmin && <p>You currently belong to {memberships.length} marketplace product team{memberships.length === 1 ? '' : 's'}.</p>}
                          <p>Open MapleOS, launch Maple Market, then use the profile menu to enter Marketplace Admin.</p>
                        </>
                      ) : (
                        <>
                          <p>You do not have vendor access yet. Start with a submission request and we can onboard you as an owner or publisher.</p>
                          <p>Once approved, your product membership grants access to the embedded Marketplace Admin inside MapleOS.</p>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button type="button" onClick={() => openPage('submit-app')}>Submit an app</Button>
                    <Button type="button" variant="outline" onClick={() => openPage('developers-api')}>API docs</Button>
                  </div>
                </>
              )}

              {page === 'developers-api' && (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    {apiSections.map((section) => (
                      <Card key={section.title}>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <section.icon className="h-5 w-5 text-primary" />
                            {section.title}
                          </CardTitle>
                          <CardDescription>{section.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <pre className="overflow-x-auto rounded-xl border border-border bg-muted/40 p-4 text-xs text-foreground">
                            <code>{section.code}</code>
                          </pre>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <Card className="mt-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg"><FileCode2 className="h-5 w-5 text-primary" />How Maple Market works</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm text-muted-foreground">
                      <p>Site admins can manage every listing. Product owners and admins can edit their own products, upload assets, and manage product team members.</p>
                      <p>Developers and moderators can access the embedded admin in read-only mode. That lets teams collaborate safely without overexposing product control.</p>
                      <p>For public-facing browsing, Maple Market storefront pages remain separate from the in-OS administration experience.</p>
                    </CardContent>
                  </Card>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button type="button" onClick={() => openHref('/docs?section=api')}>Open core docs</Button>
                    <Button type="button" variant="outline" onClick={() => openPage('submit-app')}>Start submission</Button>
                  </div>
                </>
              )}

              {page === 'submit-app' && (
                <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Vendor onboarding</CardTitle>
                        <CardDescription>{accessMessage}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm text-muted-foreground">
                        <p>Publishing on Maple Market starts with a submission request. Once approved, the product owner receives marketplace membership and can manage the listing from inside MapleOS App Store.</p>
                        <p>The embedded Marketplace Admin handles product editing, assets, publishing settings, and team management after onboarding is approved.</p>
                      </CardContent>
                    </Card>

                    {user ? (
                      <Card>
                        <CardHeader>
                          <CardTitle>Submit your app</CardTitle>
                          <CardDescription>Tell us about your app and we will use this to start publisher onboarding.</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {submitted ? (
                            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 text-sm text-muted-foreground">
                              <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
                                <ShieldCheck className="h-4 w-4 text-primary" />
                                Submission received
                              </div>
                              We have your request. Next steps usually include publisher review, product setup, and owner access being granted inside MapleOS.
                            </div>
                          ) : (
                            <Form {...form}>
                              <form className="space-y-5" onSubmit={form.handleSubmit(onSubmitApp)}>
                                <div className="grid gap-5 md:grid-cols-2">
                                  <FormField
                                    control={form.control}
                                    name="studioName"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Studio or company</FormLabel>
                                        <FormControl>
                                          <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name="appName"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>App name</FormLabel>
                                        <FormControl>
                                          <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>

                                <div className="grid gap-5 md:grid-cols-2">
                                  <FormField
                                    control={form.control}
                                    name="website"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Website</FormLabel>
                                        <FormControl>
                                          <Input {...field} placeholder="https://example.com" />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name="category"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Category</FormLabel>
                                        <Select embedded={embedded} value={field.value} onValueChange={field.onChange}>
                                          <FormControl>
                                            <SelectTrigger>
                                              <SelectValue placeholder="Select a category" />
                                            </SelectTrigger>
                                          </FormControl>
                                          <SelectContent>
                                            {submitCategories.map((category) => (
                                              <SelectItem key={category} value={category}>
                                                {category}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>

                                <FormField
                                  control={form.control}
                                  name="summary"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>What are you submitting?</FormLabel>
                                      <FormControl>
                                        <Textarea
                                          {...field}
                                          rows={8}
                                          placeholder="Describe the app, what it does, how you plan to package it, and any vendor/owner details we should know."
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <Button type="submit" disabled={submitting} className="gap-2">
                                  {submitting ? <Send className="h-4 w-4 animate-pulse" /> : <ArrowRight className="h-4 w-4" />}
                                  Start vendor onboarding
                                </Button>
                              </form>
                            </Form>
                          )}
                        </CardContent>
                      </Card>
                    ) : (
                      <Card>
                        <CardHeader>
                          <CardTitle>Sign in to continue</CardTitle>
                          <CardDescription>You need a MapleOS account before starting marketplace vendor onboarding.</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <Button type="button" onClick={() => navigate('/auth')}>Sign in or create an account</Button>
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg"><LayoutDashboard className="h-5 w-5 text-primary" />After approval</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm text-muted-foreground">
                        <p>Approved owners and site admins access management tools from inside MapleOS App Store.</p>
                        <p>From there, you can edit listings, upload assets, set release metadata, and manage product team roles.</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Helpful next steps</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Button type="button" variant="outline" className="w-full justify-start" onClick={() => openPage('developers-api')}>
                          Read Maple Market docs
                        </Button>
                        <Button type="button" variant="outline" className="w-full justify-start" onClick={() => openPage('developers')}>
                          Open Developer Portal
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              {page === 'legal-eula' && (
                <div className="space-y-6 text-sm leading-7 text-muted-foreground">
                  <Card>
                    <CardHeader><CardTitle>Effective date</CardTitle></CardHeader>
                    <CardContent>{eulaLastUpdated}</CardContent>
                  </Card>
                  <div className="rounded-2xl border border-border bg-muted/20 p-6">
                    <div className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground">
                      <EulaDocumentContent />
                    </div>
                  </div>
                  <Card>
                    <CardHeader><CardTitle>Contact</CardTitle></CardHeader>
                    <CardContent className="space-y-1">
                      <div>{eulaCompanyName}</div>
                      <div>{eulaCompanyAddress}</div>
                      <a href={`mailto:${eulaContactEmail}`} className="text-foreground underline underline-offset-4">{eulaContactEmail}</a>
                    </CardContent>
                  </Card>
                  <div className="flex flex-wrap gap-3">
                    <Button type="button" variant="outline" onClick={() => openPage('legal-data')}>Data Policy</Button>
                  </div>
                </div>
              )}

              {page === 'legal-privacy' && (
                <>
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card>
                      <CardHeader><CardTitle>Account privacy</CardTitle></CardHeader>
                      <CardContent className="text-sm text-muted-foreground">Marketplace purchases, memberships, and vendor access are tied to your MapleOS account and protected by platform access controls.</CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle>Publisher privacy</CardTitle></CardHeader>
                      <CardContent className="text-sm text-muted-foreground">Vendor onboarding, ownership, and product-team roles are handled through scoped memberships rather than public admin access.</CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle>Canadian posture</CardTitle></CardHeader>
                      <CardContent className="text-sm text-muted-foreground">MapleOS privacy positioning remains centered on Canadian infrastructure, legal safeguards, and transparent data use.</CardContent>
                    </Card>
                  </div>

                  <div className="mt-6 space-y-4 text-sm leading-7 text-muted-foreground">
                    <p>Maple Market is part of the MapleOS ecosystem, so the broader MapleOS privacy commitments continue to apply when you browse, purchase, install, or publish apps through the marketplace.</p>
                    <p>Use the full MapleOS privacy page for the canonical privacy overview, and the Data Policy for more specific operational details.</p>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button type="button" onClick={() => openHref('/privacy')}>Open privacy page</Button>
                    <Button type="button" variant="outline" onClick={() => openPage('legal-data')}>Data Policy</Button>
                  </div>
                </>
              )}

              {page === 'legal-data' && (
                <div className="space-y-4 text-sm leading-7 text-muted-foreground">
                  <Card>
                    <CardHeader><CardTitle>Effective date</CardTitle></CardHeader>
                    <CardContent>{dataLastUpdated}</CardContent>
                  </Card>
                  <div className="rounded-2xl border border-border bg-muted/20 p-6">
                    <div className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground">
                      <DataPolicyDocumentContent />
                    </div>
                  </div>
                  <Card>
                    <CardHeader><CardTitle>Questions about marketplace data</CardTitle></CardHeader>
                    <CardContent className="space-y-1">
                      <div>{companyAddress}</div>
                      <a href={`mailto:${dataContactEmail}`} className="text-foreground underline underline-offset-4">{dataContactEmail}</a>
                    </CardContent>
                  </Card>
                  <div className="flex flex-wrap gap-3">
                    <Button type="button" variant="outline" onClick={() => openPage('contact')}>Contact us</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <MapleMarketFooter onSelectCategory={onSelectCategory} onOpenPage={openPage} />
    </div>
  );
}